import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import { academicService, contentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { InfoTooltip } from '../components/ui';

function EditLesson() {
  const navigate = useNavigate();
  const { moduleId, lessonId } = useParams();
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [module, setModule] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    thumbnail: null,
    video_url: '',
    video_file: null,
    order: 0,
    duration_minutes: '',
    is_published: false,
    is_free: false,
    mobile_only: false,
  });
  const [videoUploadType, setVideoUploadType] = useState('url'); // 'url' or 'file'
  const [currentVideoFile, setCurrentVideoFile] = useState(null); // For existing video file
  const [currentThumbnail, setCurrentThumbnail] = useState(null); // For existing thumbnail
  const [thumbnailPreview, setThumbnailPreview] = useState(null); // For a newly selected thumbnail
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const isTeacher = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');
    if (!isTeacher) {
      addNotification(t('Only teachers can edit lessons'), 'error');
      navigate('/courses');
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user, moduleId, lessonId]);

  const fetchData = async () => {
    try {
      setFetchingData(true);

      // Fetch module and its categories
      const moduleResponse = await academicService.getModule(moduleId);
      const moduleData = moduleResponse.data || moduleResponse;
      setModule(moduleData);

      if (moduleData.categories && moduleData.categories.length > 0) {
        setCategories(moduleData.categories);
      } else {
        addNotification(t('This module has no categories'), 'warning');
        navigate(`/modules/${moduleId}`);
        return;
      }

      // Fetch lesson data
      const lessonResponse = await contentService.getLesson(lessonId);
      const lessonData = lessonResponse.data || lessonResponse;

      // Set form data
      setFormData({
        title: lessonData.title || '',
        content: lessonData.content || '',
        category: lessonData.category?.id || lessonData.category || '',
        thumbnail: null,
        video_url: lessonData.video_url || '',
        video_file: null,
        order: lessonData.order || 0,
        duration_minutes: lessonData.duration_minutes || '',
        is_published: lessonData.is_published || false,
        is_free: lessonData.is_free || false,
        mobile_only: lessonData.mobile_only || false,
      });
      setCurrentThumbnail(lessonData.thumbnail || null);

      // Determine video upload type based on existing data
      if (lessonData.video_file) {
        setVideoUploadType('file');
        setCurrentVideoFile(lessonData.video_file);
      } else if (lessonData.video_url) {
        setVideoUploadType('url');
      }
    } catch (error) {
      addNotification(t('Failed to load lesson data'), 'error');
      navigate(`/modules/${moduleId}`);
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 1GB)
      if (file.size > 1024 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, video_file: t('Video file size must be less than 1GB') }));
        return;
      }
      setFormData(prev => ({ ...prev, video_file: file }));
      setErrors(prev => ({ ...prev, video_file: '' }));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, thumbnail: t('Thumbnail image must be less than 5MB') }));
      return;
    }
    setFormData(prev => ({ ...prev, thumbnail: file }));
    setErrors(prev => ({ ...prev, thumbnail: '' }));
    setThumbnailPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = t('Lesson title is required');
    if (!formData.content.trim()) newErrors.content = t('Lesson content is required');
    if (!formData.category) newErrors.category = t('Category is required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const isUploadingFile = videoUploadType === 'file' && !!formData.video_file;

    setLoading(true);
    if (isUploadingFile) setUploadProgress(0);
    try {
      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('content', formData.content);
      submitFormData.append('category', parseInt(formData.category));
      submitFormData.append('order', formData.order || 0);
      submitFormData.append('is_published', formData.is_published);
      submitFormData.append('is_free', formData.is_free);

      if (formData.duration_minutes) {
        submitFormData.append('duration_minutes', formData.duration_minutes);
      }

      if (formData.thumbnail) {
        // Only append if a new thumbnail was selected — otherwise leave the
        // existing one untouched instead of clearing it on every save.
        submitFormData.append('thumbnail', formData.thumbnail);
      }

      // Add video based on upload type
      if (videoUploadType === 'url') {
        submitFormData.append('video_url', formData.video_url || '');
        // Clear video_file if switching to URL
        submitFormData.append('video_file', '');
      } else if (videoUploadType === 'file') {
        if (formData.video_file) {
          // Only append new video file if user selected one
          submitFormData.append('video_file', formData.video_file);
        }
        // Clear video_url if using file
        submitFormData.append('video_url', '');
        submitFormData.append('mobile_only', formData.mobile_only);
      }

      await contentService.updateLesson(lessonId, submitFormData, isUploadingFile ? {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      } : undefined);

      addNotification(t('Lesson updated successfully!'), 'success');
      navigate(`/modules/${moduleId}`);
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        setErrors(errorData);
        // Show first error message
        const firstError = Object.values(errorData)[0];
        addNotification(`${t('Failed')}: ${typeof firstError === 'string' ? firstError : JSON.stringify(firstError)}`, 'error');
      } else if (error.message) {
        addNotification(`${t('Failed to update lesson')}: ${error.message}`, 'error');
      } else {
        addNotification(t('Failed to update lesson. Please try again.'), 'error');
      }
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Edit Lesson')} subtitle={t('Loading...')} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Edit Lesson')} subtitle={t('Update lesson in {{module}}', { module: module?.name || '' })} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Lesson Title')} *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., Introduction to Linear Equations')}
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Category')} *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.category ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Thumbnail (optional)')}
                  </label>
                  <div className="flex items-center gap-4">
                    {thumbnailPreview || currentThumbnail ? (
                      <img src={thumbnailPreview || currentThumbnail} alt="" className="w-20 h-20 rounded-lg object-cover border border-gray-200 shrink-0" />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div>
                      <label htmlFor="thumbnail" className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        {thumbnailPreview || currentThumbnail ? t('Change image') : t('Choose image')}
                      </label>
                      <input
                        id="thumbnail"
                        name="thumbnail"
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                        className="hidden"
                      />
                      <p className="mt-1 text-xs text-gray-500">{t('Shown in the course\'s lesson list. Defaults to a placeholder if not set. Max 5MB.')}</p>
                      {errors.thumbnail && <p className="mt-1 text-sm text-red-600">{errors.thumbnail}</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Lesson Content')} *
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={8}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.content ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('Enter the lesson content here...')}
                  />
                  {errors.content && <p className="mt-1 text-sm text-red-600">{errors.content}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Video (Optional)')}
                  </label>

                  {/* Toggle between URL and File Upload */}
                  <div className="flex gap-4 mb-3">
                    <button
                      type="button"
                      onClick={() => setVideoUploadType('url')}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        videoUploadType === 'url'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      📎 {t('URL Link')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoUploadType('file')}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        videoUploadType === 'file'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      📁 {t('Upload File')}
                    </button>
                  </div>

                  {videoUploadType === 'url' ? (
                    <input
                      type="url"
                      id="video_url"
                      name="video_url"
                      value={formData.video_url}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    />
                  ) : (
                    <div>
                      {currentVideoFile && !formData.video_file && (
                        <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                          <p className="text-sm text-gray-700">
                            {t('Current video')}: <span className="font-medium">{currentVideoFile.split('/').pop()}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{t('Upload a new file to replace it')}</p>
                        </div>
                      )}
                      <input
                        type="file"
                        id="video_file"
                        accept="video/mp4,video/avi,video/mov,video/webm"
                        onChange={handleFileChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      {formData.video_file && (
                        <p className="mt-2 text-sm text-green-600 font-medium">
                          {t('New video selected')}: {formData.video_file.name} ({(formData.video_file.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                      {errors.video_file && <p className="mt-1 text-sm text-red-600">{errors.video_file}</p>}
                      <p className="mt-1 text-xs text-gray-500">
                        {t('Supported formats: MP4, AVI, MOV, WebM (Max 1GB)')}
                      </p>
                      <label className="mt-3 flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="mobile_only"
                          checked={formData.mobile_only}
                          onChange={handleChange}
                          className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700">{t('Mobile app only')}</span>
                        <InfoTooltip text={t('Restricts this video to the mobile app, where screen recording can actually be blocked or detected. Browsers have no way to do either, so on the website students will see a message instead of the video.')} />
                      </label>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Display Order')}
                    </label>
                    <input
                      type="number"
                      id="order"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="duration_minutes" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Duration (minutes)')}
                    </label>
                    <input
                      type="number"
                      id="duration_minutes"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="30"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={formData.is_published}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('Published')}</span>
                  </label>

                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_free"
                      checked={formData.is_free}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('Free preview')}</span>
                  </label>
                </div>

                {uploadProgress !== null && (
                  <div className="pt-2">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1.5">
                      <span>{uploadProgress < 100 ? t('Uploading video...') : t('Processing...')}</span>
                      <span className="font-medium tabular-nums">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/modules/${moduleId}`)}
                    disabled={loading}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="min-w-[9.5rem] inline-flex items-center justify-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading && (
                      <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    )}
                    {loading ? t('Updating...') : t('Update Lesson')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditLesson;
